import React from 'react';
import Box from '@mui/material/Box';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';

interface LockToggleButtonProps {
  isLocked: boolean;
  isDisabled: boolean;
  lockReason: string | null;
  onToggle: () => void;
  isLoading?: boolean;
}

export const LockToggleButton: React.FC<LockToggleButtonProps> = ({
  isLocked,
  isDisabled,
  lockReason,
  onToggle,
  isLoading = false,
}) => {
  // When disabled, always show locked state
  const displayLocked = isDisabled ? true : isLocked;
  
  const tooltipTitle = isDisabled && lockReason 
    ? lockReason 
    : displayLocked
    ? 'Click to unlock' 
    : 'Click to lock';

  return (
    <Tooltip title={tooltipTitle} arrow>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 0.5,
          py: 0.25,
          borderRadius: 0.5,
          opacity: isDisabled ? 0.5 : 1,
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: !isDisabled ? 'action.hover' : 'action.disabledBackground',
          },
        }}
        onClick={!isDisabled && !isLoading ? onToggle : undefined}
      >
        {displayLocked ? (
          <LockIcon sx={{ fontSize: 15, color: 'primary.main' }} />
        ) : (
          <LockOpenIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
        )}
        
        <Switch
          size="small"
          checked={displayLocked}
          disabled={isDisabled || isLoading}
          onClick={(e) => {
            // Prevent parent Box onClick from firing when user clicks the switch.
            e.stopPropagation();
          }}
          onChange={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          sx={{ ml: 0.25 }}
        />

        <Typography
          variant="caption"
          sx={{
            fontSize: '0.72rem',
            fontWeight: displayLocked ? 600 : 400,
            color: isDisabled ? 'text.disabled' : 'text.primary',
            userSelect: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          {displayLocked ? 'Locked' : 'Unlocked'}
        </Typography>
      </Box>
    </Tooltip>
  );
};
